// src/components/initial-inventory/ProductSearchCreate.tsx
// Product search and selection component for Initial Inventory workflow
// Allows searching existing products or triggering create flow

import React, { useState, useEffect, useRef } from 'react';
import { Search, Package, ChevronDown, X, Plus, Check } from 'lucide-react';
import { Product } from '../../services/productCatalogAPI';
import { CategoryHierarchy } from '../../types/category';
import { getItemTrackingConfig } from '../../utils/categoryTracking';

interface ProductSearchCreateProps {
  products: Product[];
  categories: CategoryHierarchy[];
  selectedProduct: Product | null;
  onProductSelected: (product: Product | null) => void;
  onCreateProduct: () => void;
}

const ProductSearchCreate: React.FC<ProductSearchCreateProps> = ({
  products,
  categories,
  selectedProduct,
  onProductSelected,
  onCreateProduct
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter products when search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products.slice(0, 50)); // Show first 50 when no search
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = products.filter(p => 
      p.name.toLowerCase().includes(term) ||
      p.manufacturer?.toLowerCase().includes(term) ||
      p.model?.toLowerCase().includes(term)
    );
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCategoryPath = (categoryId: string): string => {
    const category = categories.find(c => c.categoryId === categoryId);
    if (!category) return '';
    
    if (category.parentCategoryId) {
      const parent = categories.find(c => c.categoryId === category.parentCategoryId);
      return parent ? `${parent.name} › ${category.name}` : category.name;
    }
    
    return category.name;
  };

  const getTrackingTypeBadge = (product: Product) => {
    // Try to get tracking type from product or category
    let trackingType = product.trackingType;
    
    if (!trackingType) {
      const subcategory = categories.find(c => c.categoryId === product.subcategoryId);
      if (subcategory) {
        const config = getItemTrackingConfig(subcategory);
        trackingType = config.trackingType;
      } else {
        trackingType = product.productType === 'SERIALIZED' ? 'SERIALIZED' : 'BULK_TRACKED';
      }
    }

    const styles = {
      SERIALIZED: 'bg-blue-100 text-blue-700',
      BULK_TRACKED: 'bg-green-100 text-green-700',
      CONSUMABLE: 'bg-orange-100 text-orange-700'
    };

    const labels = {
      SERIALIZED: 'Serialized',
      BULK_TRACKED: 'Bulk',
      CONSUMABLE: 'Consumable'
    };

    return (
      <span className={`text-xs px-1.5 py-0.5 rounded ${styles[trackingType]}`}>
        {labels[trackingType]}
      </span>
    );
  };

  const handleSelect = (product: Product) => {
    onProductSelected(product);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onProductSelected(null);
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Group products by category for display
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const categoryPath = getCategoryPath(product.categoryId) || 'Uncategorized';
    if (!acc[categoryPath]) {
      acc[categoryPath] = [];
    }
    acc[categoryPath].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  // If a product is selected, show the selected state
  if (selectedProduct) {
    return (
      <div className="relative" ref={containerRef}>
        <div className="flex items-center justify-between p-4 border-2 border-blue-500 rounded-lg bg-blue-50">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900 truncate">
                  {selectedProduct.name}
                </span>
                {getTrackingTypeBadge(selectedProduct)}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {selectedProduct.manufacturer && `${selectedProduct.manufacturer} `}
                {selectedProduct.model && `• ${selectedProduct.model} `}
                • {getCategoryPath(selectedProduct.categoryId)}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
            <Check className="w-5 h-5 text-blue-600" />
            <button
              type="button"
              onClick={handleClear}
              className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
              title="Clear selection"
            >
              <X className="w-4 h-4 text-blue-600" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder="Search products by name, manufacturer, or model..."
          className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
        >
          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-y-auto">
          {filteredProducts.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">
                {searchTerm ? 'No products match your search' : 'No products in catalog'}
              </p>
              <button
                type="button"
                onClick={() => {
                  onCreateProduct();
                  setIsOpen(false);
                }}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Create New Product
              </button>
            </div>
          ) : (
            <>
              {Object.entries(groupedProducts).map(([categoryPath, categoryProducts]) => (
                <div key={categoryPath}>
                  <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide sticky top-0 border-b border-gray-100">
                    {categoryPath}
                  </div>
                  {categoryProducts.map(product => (
                    <button
                      key={product.productId}
                      type="button"
                      onClick={() => handleSelect(product)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-center justify-between group border-b border-gray-50 last:border-0"
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <Package className="w-5 h-5 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">
                            {product.name}
                          </div>
                          {(product.manufacturer || product.model) && (
                            <div className="text-sm text-gray-500 truncate">
                              {[product.manufacturer, product.model].filter(Boolean).join(' • ')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                        {getTrackingTypeBadge(product)}
                      </div>
                    </button>
                  ))}
                </div>
              ))}

              {/* Create New Product Option */}
              <div className="border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => {
                    onCreateProduct();
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-2 text-blue-600"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">
                    {searchTerm ? `Create "${searchTerm}" as new product` : 'Create new product'}
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSearchCreate;
